/**
 * Number of empty rows needed to fill the visible part of a table container:
 * (container height - height of the data rows) / ROW_HEIGHT.
 *
 * Measured once per data change, never on resize: the container is not bounded
 * by the viewport (a long list makes the page grow), so observing its size
 * would feed the filler rows back into the measure.
 */
import { useLayoutEffect, useState, type RefObject } from 'react';

export const ROW_HEIGHT = 34;

export const fillerRowCount = (
  containerHeight: number,
  rowsHeight: number,
  rowHeight = ROW_HEIGHT,
): number =>
  Math.max(0, Math.floor((containerHeight - rowsHeight) / rowHeight));

const totalHeight = (elements: Iterable<HTMLElement>): number =>
  Array.from(elements).reduce(
    (total, element) => total + element.offsetHeight,
    0,
  );

export const useFillerRows = (
  containerRef: RefObject<HTMLElement | null>,
  rowSelector: string,
  dataRowCount: number,
): number => {
  const [count, setCount] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // The filler rows of the previous measure are part of the container height.
    const fillers = totalHeight(
      container.querySelectorAll<HTMLElement>('tr.empty-row'),
    );
    const rows = totalHeight(
      container.querySelectorAll<HTMLElement>(rowSelector),
    );
    setCount(fillerRowCount(container.offsetHeight - fillers, rows));
  }, [containerRef, rowSelector, dataRowCount]);

  return count;
};
